import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, timer, of, Subject } from 'rxjs';
import { switchMap, debounce, distinctUntilChanged, catchError, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Tag, CreateTagRequest, UpdateGameTagsRequest } from '../models/tag.model';

@Injectable({
  providedIn: 'root'
})
export class TagService implements OnDestroy {
  private apiUrl = `${environment.apiUrl}/tags`;
  private searchSubject = new BehaviorSubject<string>('');
  private debounceTime = 300;
  private destroy$ = new Subject<void>();

  constructor(private http: HttpClient) {
    this.setupSearchDebounce();
  }

  private setupSearchDebounce(): void {
    this.searchSubject
      .pipe(
        debounce(() => timer(this.debounceTime)),
        distinctUntilChanged(),
        switchMap(searchTerm => this.getTags({ search: searchTerm.trim() })),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  searchTags(searchTerm: string, type: string = 'quiz'): void {
    this.searchSubject.next(searchTerm);
  }

  getTags(params: {
    type?: string;
    search?: string;
    limit?: number;
  } = {}): Observable<Tag[]> {
    let httpParams = new HttpParams();

    if (params.type) {
      httpParams = httpParams.set('type', params.type);
    }
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return this.http.get<Tag[]>(this.apiUrl, { params: httpParams })
      .pipe(
        catchError(() => of([]))
      );
  }

  createTag(tagData: CreateTagRequest): Observable<Tag> {
    return this.http.post<Tag>(this.apiUrl, tagData);
  }

  updateGameTags(updateData: UpdateGameTagsRequest): Observable<Tag[]> {
    return this.http.put<Tag[]>(`${this.apiUrl}/game`, updateData);
  }

  getGameTags(gameId: number): Observable<Tag[]> {
    return this.http.get<Tag[]>(`${this.apiUrl}/game/${gameId}`)
      .pipe(
        catchError(() => of([]))
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}